import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import FieldHelpTooltip from "../Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../Help/HelpContent";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getServiceCategories,
  getSubCategories,
  getServiceTypes,
  getSessionOptions,
  createSubscription,
  editSubscription,
  getPartners,
} from "../../services/subscriptionService";
import * as apicall from "../Apicall";
import { DAYS, deriveScheduleMessage } from "../../utils/scheduleDateMath";

const FIXED_TYPES = ["Weekly", "Monthly", "Quarterly", "Half Yearly"];
const SESSION_TYPES = ["Limited", "Unlimited"];
const RECURRING_DUE_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

// const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
//   const totalMins = 360 + i * 30; // 6:00 AM → 10:00 PM
//   const h = Math.floor(totalMins / 60);
//   const m = totalMins % 60 === 0 ? "00" : "30";
//   const ampm = h < 12 ? "AM" : "PM";
//   return `${h % 12 || 12}:${m} ${ampm}`;
// });

const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const totalMins = 360 + i * 30; // 06:00 → 22:00
  const hours = String(Math.floor(totalMins / 60)).padStart(2, "0");
  const mins = String(totalMins % 60).padStart(2, "0");

  return `${hours}:${mins}`;
});

const inputStyle = {
  width: "100%",
  height: "44px",
  border: "1px solid #D1D5DB",
  borderRadius: "8px",
  padding: "0 12px",
  fontSize: "14px",
  color: "#374151",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#374151",
  marginBottom: "6px",
};

const errorStyle = {
  color: "#EF4444",
  fontSize: "12px",
  marginTop: "4px",
};

const ToggleBtn = ({ label, active, onClick, disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    style={{
      flex: 1,
      padding: "9px 12px",
      border: `1px solid ${active ? "#C0335A" : "#E0E0E0"}`,
      borderRadius: "8px",
      background: active ? "#FDE8EF" : disabled ? "#F9FAFB" : "#fff",
      color: active ? "#C0335A" : disabled ? "#B0B7C3" : "#374151",
      fontWeight: active ? "600" : "400",
      fontSize: "14px",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.7 : 1,
      transition: "all 0.15s",
    }}
  >
    {label}
  </button>
);

const buildInitialForm = (isEditMode, initialData) => {
  if (isEditMode && initialData) {
    let schedule = [];
    try { schedule = JSON.parse(initialData.fixed_schedule || "[]"); } catch { schedule = []; }
    return {
      planName: initialData.name || "",
      serviceId: String(initialData.service || ""),
      serviceTypeId: String(initialData.service_type || ""),
      subCategoryId: String(initialData.service_offering || ""),
      subscriptionType: initialData.subscription_type || "Fixed",
      fixedType: initialData.fixed_type === "HalfYearly" ? "Half Yearly" : (initialData.fixed_type || "Monthly"),
      startDate: initialData.start_date ? new Date(initialData.start_date) : null,
      endDate: initialData.end_date ? new Date(initialData.end_date) : null,
      fixedSchedule: schedule,
      fixedScheduleTime: initialData.fixed_schedule_time || "",
      pricing: String(initialData.pricing || ""),
      paymentType: initialData.payment_type || "Full",
      recurringAmount: String(initialData.recurring_amount || ""),
      recurringDueDay: String(initialData.recurring_due_day || ""),
      numberOfSessions: initialData.number_of_sessions || "Limited",
      totalSessions: String(initialData.total_sessions || ""),
      scheduleProviderId: initialData.schedule_provider_id ? String(initialData.schedule_provider_id) : "",
    };
  }
  return {
    planName: "",
    serviceId: "",
    serviceTypeId: "",
    subCategoryId: "",
    subscriptionType: "Fixed",
    fixedType: "Monthly",
    startDate: null,
    endDate: null,
    fixedSchedule: [],
    fixedScheduleTime: "",
    pricing: "",
    paymentType: "Full",
    recurringAmount: "",
    recurringDueDay: "",
    numberOfSessions: "Limited",
    totalSessions: "",
    scheduleProviderId: "",
  };
};

const CreateSubscriptionModal = ({ onClose, onSuccess, currency = "AED", isEditMode = false, initialData = null, industry }) => {
  const isClinicService = Number(industry) === 1;
  const [serviceCategories, setServiceCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [form, setForm] = useState(() => buildInitialForm(isEditMode, initialData));

  const [errors, setErrors] = useState({});

  // Load all static dropdowns on mount; in edit mode also pre-load dependent dropdowns
  useEffect(() => {
    getPartners()
      .then(res => {
        const partners = res.data || [];
        const selfPartner = partners.find(p => p.partner_name === "Self");
        return getServiceCategories(selfPartner?.id);
      })
      .then(res => setServiceCategories(res.data || []))
      .catch(() => {});
    getSessionOptions()
      .then(res => setSessionOptions(res.data || []))
      .catch(() => {});

    if (isEditMode && initialData?.service) {
      getSubCategories(initialData.service)
        .then(res => setSubCategories(res.data || []))
        .catch(() => {});
      // Use saved sub-category (service_type) to get the correctly filtered service offerings
      getServiceTypes(initialData.service, initialData.service_type || "")
        .then(res => setServiceTypes(res.data || []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load crew/doctor dropdown for Fixed plans, scoped to the selected service category
  useEffect(() => {
    if (form.subscriptionType !== "Fixed" || !form.serviceId) {
      setCrewList([]);
      return;
    }
    let cancelled = false;
    const loadCrew = async () => {
      try {
        setCrewLoading(true);
        const res = await apicall.get(`get_ezp_service_providers?limit=100&page=1&skill=PROFESSIONAL&services=${form.serviceId}`);
        if (cancelled) return;
        if (res?.status === 200 && Array.isArray(res.data)) {
          setCrewList(res.data.map(c => {
            const name = c.provider_name ? c.provider_name.replace(",", " ").trim()
              : `${c.first_name || ""} ${c.last_name || ""}`.trim();
            return { label: name || "Unknown", value: c.service_provider };
          }));
        } else {
          setCrewList([]);
        }
      } catch {
        if (!cancelled) setCrewList([]);
      } finally {
        if (!cancelled) setCrewLoading(false);
      }
    };
    loadCrew();
    return () => { cancelled = true; };
  }, [form.subscriptionType, form.serviceId]);

  // Explicit handler for service category change — resets and reloads dependent dropdowns
  const handleServiceChange = (value) => {
    setIsDirty(true);
    setErrors(e => ({ ...e, serviceId: undefined, serviceTypeId: undefined, subCategoryId: undefined }));
    setForm(f => ({ ...f, serviceId: value, serviceTypeId: "", subCategoryId: "" }));
    if (value) {
      getSubCategories(value).then(res => setSubCategories(res.data || [])).catch(() => {});
      getServiceTypes(value, "").then(res => setServiceTypes(res.data || [])).catch(() => {});
    } else {
      setSubCategories([]);
      setServiceTypes([]);
    }
  };

  // Explicit handler for sub-category change — resets and reloads service offerings
  const handleSubCategoryChange = (value) => {
    setIsDirty(true);
    setErrors(e => ({ ...e, serviceTypeId: undefined, subCategoryId: undefined }));
    setForm(f => ({ ...f, serviceTypeId: value, subCategoryId: "" }));
    if (form.serviceId) {
      getServiceTypes(form.serviceId, value || "").then(res => setServiceTypes(res.data || [])).catch(() => {});
    }
  };

  const setField = (key, val) => {
    setIsDirty(true);
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: undefined }));
  };

  const toggleDay = (day) => {
    setIsDirty(true);
    setForm(f => ({
      ...f,
      fixedSchedule: f.fixedSchedule.includes(day)
        ? f.fixedSchedule.filter(d => d !== day)
        : [...f.fixedSchedule, day],
    }));
    setErrors(e => ({ ...e, fixedSchedule: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.planName || !form.planName.trim()) errs.planName = "Plan name is required";
    if (!form.serviceId) errs.serviceId = "Service category is required";
    // sub category (serviceTypeId) is optional
    if (!form.subCategoryId) errs.subCategoryId = "Service type is required";
    if (form.subscriptionType === "Fixed") {
      if (!form.startDate) errs.startDate = "Start date is required";
      if (!form.endDate) errs.endDate = "End date is required";
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate)
      errs.endDate = "End date must be after start date";
    const isRecurringEligible = form.subscriptionType === "Fixed";
    const isRecurring = isRecurringEligible && form.paymentType === "Recurring";
    if (!isRecurring && (!form.pricing || isNaN(Number(form.pricing)) || Number(form.pricing) <= 0))
      errs.pricing = "Valid pricing is required";
    if (isRecurring) {
      if (!form.recurringAmount || isNaN(Number(form.recurringAmount)) || Number(form.recurringAmount) <= 0)
        errs.recurringAmount = "Valid recurring amount is required";
      if (!form.recurringDueDay) errs.recurringDueDay = "Due day is required";
    }
    if (form.subscriptionType === "Fixed") {
      if (!form.fixedType) errs.fixedType = "Select a type";
      if (form.fixedSchedule.length === 0) errs.fixedSchedule = "Select at least one day";
      if (!form.fixedScheduleTime) errs.fixedScheduleTime = "Select a time slot";
    }
    if (form.subscriptionType === "Open") {
      if (form.numberOfSessions === "Limited" && !form.totalSessions)
        errs.totalSessions = "Total sessions is required";
    }
    return errs;
  };

  const isRecurringEligible = form.subscriptionType === "Fixed";
  const isRecurring = isRecurringEligible && form.paymentType === "Recurring";
  const lockPaymentType = isEditMode && Number(initialData?.active_subscriber_count || 0) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.planName.trim(),
        service: Number(form.serviceId),
        service_type: form.serviceTypeId ? Number(form.serviceTypeId) : 0,
        service_offering: Number(form.subCategoryId),
        subscription_type: form.subscriptionType,
        start_date: form.startDate?.toISOString().split("T")[0],
        end_date: form.endDate?.toISOString().split("T")[0],
        pricing: isRecurring ? 0 : Number(form.pricing),
        payment_type: isRecurringEligible ? form.paymentType : "Full",
        currency,
      };
      if (isRecurring) {
        payload.recurring_amount = Number(form.recurringAmount);
        payload.recurring_due_day = Number(form.recurringDueDay);
      }
      if (form.subscriptionType === "Fixed") {
        payload.fixed_type = form.fixedType === "Half Yearly" ? "HalfYearly" : form.fixedType;
        payload.fixed_schedule = JSON.stringify(form.fixedSchedule);
        payload.fixed_schedule_time = form.fixedScheduleTime;
        payload.schedule_provider_id = form.scheduleProviderId ? Number(form.scheduleProviderId) : null;
      } else {
        payload.number_of_sessions = form.numberOfSessions;
        if (form.numberOfSessions === "Limited") {
          payload.total_sessions = Number(form.totalSessions);
        }
      }
      if (isEditMode) {
        payload.id = initialData.id;
        await editSubscription(payload);
        toast.success("Subscription plan updated successfully");
      } else {
        await createSubscription(payload);
        toast.success("Subscription plan created successfully");
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || `Failed to ${isEditMode ? "update" : "create"} subscription plan`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Close anyway?")) return;
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1040,
        }}
      />

      {/* Drawer panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",  // panel itself does NOT scroll
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #F3F4F6",
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 1,
          }}
        >
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827" }}>
            {isEditMode ? "Edit Subscription Plan" : "Create Subscription Plan"}
          </h2>
          <button
            onClick={handleBackdropClick}
            style={{
              background: "none",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              width: "36px",
              height: "36px",
              fontSize: "18px",
              cursor: "pointer",
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Form — fields area scrolls, submit button is in sticky footer below */}
        <form onSubmit={handleSubmit} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* Plan Name */}
          <div>
            <label style={labelStyle}>Subscription Plan Name <FieldHelpTooltip {...HELP_CONTENT.createSubscription.planName} /></label>
            <input
              type="text"
              value={form.planName}
              onChange={e => setField("planName", e.target.value)}
              placeholder="e.g. Monthly Pest Control Plan"
              style={inputStyle}
            />
            {errors.planName && <div style={errorStyle}>{errors.planName}</div>}
          </div>

          {/* Service Category */}
          <div>
            <label style={labelStyle}>Select a Service Category <FieldHelpTooltip {...HELP_CONTENT.createSubscription.serviceOffering} /></label>
            <select
              value={form.serviceId}
              onChange={e => handleServiceChange(e.target.value)}
              style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
            >
              <option value="">-- Select Service Category --</option>
              {serviceCategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {errors.serviceId && <div style={errorStyle}>{errors.serviceId}</div>}
          </div>

          {/* Sub Category — optional */}
          <div>
            <label style={labelStyle}>
              Select Sub Category
              <span style={{ fontWeight: "400", color: "#9CA3AF", marginLeft: "6px" }}>(optional)</span>
            </label>
            <select
              value={form.serviceTypeId}
              onChange={e => handleSubCategoryChange(e.target.value)}
              disabled={!form.serviceId}
              style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", opacity: !form.serviceId ? 0.5 : 1 }}
            >
              <option value="">-- All Sub Categories --</option>
              {subCategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Service Type — enabled as soon as service category is selected */}
          <div>
            <label style={labelStyle}>Select a Service Type</label>
            <select
              value={form.subCategoryId}
              onChange={e => setField("subCategoryId", e.target.value)}
              disabled={!form.serviceId}
              style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", opacity: !form.serviceId ? 0.5 : 1 }}
            >
              <option value="">-- Select Service Type --</option>
              {serviceTypes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {errors.subCategoryId && <div style={errorStyle}>{errors.subCategoryId}</div>}
          </div>

          {/* Subscription Type */}
          <div>
            <label style={labelStyle}>Subscription Type <FieldHelpTooltip {...HELP_CONTENT.createSubscription.subscriptionType} /></label>
            <div style={{ display: "flex", gap: "8px" }}>
              <ToggleBtn label="Fixed" active={form.subscriptionType === "Fixed"} onClick={() => { setField("subscriptionType", "Fixed"); }} />
              <ToggleBtn label="Open" active={form.subscriptionType === "Open"} onClick={() => { setField("subscriptionType", "Open"); setField("paymentType", "Full"); }} />
            </div>
          </div>

          {/* Fixed fields */}
          {form.subscriptionType === "Fixed" && (
            <>
              <div>
                <label style={labelStyle}>Frequency</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  {FIXED_TYPES.map(t => (
                    <ToggleBtn
                      key={t}
                      label={t}
                      active={form.fixedType === t}
                      onClick={() => setField("fixedType", t)}
                    />
                  ))}
                </div>
                {errors.fixedType && <div style={errorStyle}>{errors.fixedType}</div>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={form.startDate}
                      onChange={date => setField("startDate", date)}
                      dateFormat="d MMMM yyyy"
                      placeholderText="e.g. 15 January 2025"
                      customInput={
                        <input style={inputStyle} />
                      }
                    />
                  </div>
                  {errors.startDate && <div style={errorStyle}>{errors.startDate}</div>}
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <DatePicker
                    selected={form.endDate}
                    onChange={date => setField("endDate", date)}
                    dateFormat="d MMMM yyyy"
                    minDate={form.startDate}
                    placeholderText="e.g. 15 January 2026"
                    customInput={<input style={inputStyle} />}
                  />
                  {errors.endDate && <div style={errorStyle}>{errors.endDate}</div>}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Fixed Schedule</label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {DAYS.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: "7px 12px",
                        border: `1px solid ${form.fixedSchedule.includes(day) ? "#C0335A" : "#E0E0E0"}`,
                        borderRadius: "8px",
                        background: form.fixedSchedule.includes(day) ? "#FDE8EF" : "#fff",
                        color: form.fixedSchedule.includes(day) ? "#C0335A" : "#374151",
                        fontWeight: form.fixedSchedule.includes(day) ? "600" : "400",
                        fontSize: "13px",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                {errors.fixedSchedule && <div style={errorStyle}>{errors.fixedSchedule}</div>}

                {/* Schedule hint messages */}
                {form.fixedType === "Weekly" && (
                  <div style={{ marginTop: "10px", padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "8px", fontSize: "13px", color: "#1D4ED8" }}>
                    Tip: If a daily subscription is needed, select all days to schedule it every day.
                  </div>
                )}
                {["Monthly", "Quarterly", "Half Yearly"].includes(form.fixedType) && (() => {
                  const msg = deriveScheduleMessage(form.fixedType, form.startDate, form.fixedSchedule);
                  return msg ? (
                    <div style={{ marginTop: "10px", padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", fontSize: "13px", color: "#15803D" }}>
                      {msg}
                    </div>
                  ) : null;
                })()}
              </div>

              <div>
                <label style={labelStyle}>Fixed Schedule Time Slot</label>
                <select
                  value={form.fixedScheduleTime}
                  onChange={e => setField("fixedScheduleTime", e.target.value)}
                  style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
                >
                  <option value="">-- Select Time Slot --</option>
                  {TIME_SLOTS.map(slot => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
                {errors.fixedScheduleTime && <div style={errorStyle}>{errors.fixedScheduleTime}</div>}
              </div>

              <div>
                <label style={labelStyle}>
                  {isClinicService ? "Doctor Assignment" : "Crew Assignment"}
                  <span style={{ fontWeight: "400", color: "#9CA3AF", marginLeft: "6px" }}>(optional)</span>
                </label>
                <select
                  value={form.scheduleProviderId || ""}
                  onChange={e => setField("scheduleProviderId", e.target.value)}
                  disabled={crewLoading}
                  style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
                >
                  <option value="">
                    {crewLoading
                      ? (isClinicService ? "Loading doctors…" : "Loading crew…")
                      : (isClinicService ? "Select doctor (optional)" : "Select crew member (optional)")}
                  </option>
                  {crewList.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Open fields */}
          {form.subscriptionType === "Open" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={labelStyle}>
                    Start Date
                    <span style={{ fontWeight: "400", color: "#9CA3AF", marginLeft: "6px" }}>(optional)</span>
                  </label>
                  <DatePicker
                    selected={form.startDate}
                    onChange={date => setField("startDate", date)}
                    dateFormat="d MMMM yyyy"
                    placeholderText="e.g. 15 January 2025"
                    customInput={<input style={inputStyle} />}
                  />
                  {errors.startDate && <div style={errorStyle}>{errors.startDate}</div>}
                </div>
                <div>
                  <label style={labelStyle}>
                    End Date
                    <span style={{ fontWeight: "400", color: "#9CA3AF", marginLeft: "6px" }}>(optional)</span>
                  </label>
                  <DatePicker
                    selected={form.endDate}
                    onChange={date => setField("endDate", date)}
                    dateFormat="d MMMM yyyy"
                    minDate={form.startDate}
                    placeholderText="e.g. 15 January 2026"
                    customInput={<input style={inputStyle} />}
                  />
                  {errors.endDate && <div style={errorStyle}>{errors.endDate}</div>}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Number of Sessions</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {SESSION_TYPES.map(t => (
                    <ToggleBtn key={t} label={t} active={form.numberOfSessions === t} onClick={() => setField("numberOfSessions", t)} />
                  ))}
                </div>
              </div>

              {form.numberOfSessions === "Limited" && (
                <div>
                  <label style={labelStyle}>Total Sessions</label>
                  <select
                    value={form.totalSessions}
                    onChange={e => setField("totalSessions", e.target.value)}
                    style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
                  >
                    <option value="">-- Select --</option>
                    {sessionOptions.length > 0
                      ? sessionOptions.map(o => <option key={o} value={o}>{o}</option>)
                      : Array.from({ length: 50 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)
                    }
                  </select>
                  {errors.totalSessions && <div style={errorStyle}>{errors.totalSessions}</div>}
                </div>
              )}
            </>
          )}

          {/* Payment Type — Fixed plans only; any visit frequency can be billed monthly */}
          {isRecurringEligible && (
            <div>
              <label style={labelStyle}>
                Payment Type <FieldHelpTooltip {...HELP_CONTENT.createSubscription.paymentType} />
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <ToggleBtn
                  label="Full Payment"
                  active={form.paymentType === "Full"}
                  disabled={lockPaymentType}
                  onClick={() => setField("paymentType", "Full")}
                />
                <ToggleBtn
                  label="Monthly Recurring"
                  active={form.paymentType === "Recurring"}
                  disabled={lockPaymentType}
                  onClick={() => setField("paymentType", "Recurring")}
                />
              </div>
              {lockPaymentType && (
                <div style={{ marginTop: "6px", fontSize: "12px", color: "#9CA3AF" }}>
                  Payment type is locked because this plan already has active subscribers.
                </div>
              )}

              {isRecurring && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "16px" }}>
                  <div>
                    <label style={labelStyle}>
                      Monthly Recurring Amount <FieldHelpTooltip {...HELP_CONTENT.createSubscription.recurringAmount} />
                    </label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <span style={{ position: "absolute", left: "12px", fontSize: "14px", color: "#6B7280", fontWeight: "600", pointerEvents: "none" }}>
                        {currency} |
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.recurringAmount}
                        onChange={e => setField("recurringAmount", e.target.value)}
                        placeholder="0.00"
                        style={{ ...inputStyle, paddingLeft: "72px" }}
                      />
                    </div>
                    {errors.recurringAmount && <div style={errorStyle}>{errors.recurringAmount}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>
                      Due Day of Month <FieldHelpTooltip {...HELP_CONTENT.createSubscription.recurringDueDay} />
                    </label>
                    <select
                      value={form.recurringDueDay}
                      onChange={e => setField("recurringDueDay", e.target.value)}
                      style={{ ...inputStyle, paddingRight: "36px", appearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
                    >
                      <option value="">-- Select Day --</option>
                      {RECURRING_DUE_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {errors.recurringDueDay && <div style={errorStyle}>{errors.recurringDueDay}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pricing — not applicable for Monthly Recurring plans */}
          {!isRecurring && (
            <div>
              <label style={labelStyle}>Pricing <FieldHelpTooltip {...HELP_CONTENT.createSubscription.pricing} /></label>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <span
                  style={{
                    position: "absolute",
                    left: "12px",
                    fontSize: "14px",
                    color: "#6B7280",
                    fontWeight: "600",
                    pointerEvents: "none",
                  }}
                >
                  {currency} |
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.pricing}
                  onChange={e => setField("pricing", e.target.value)}
                  placeholder="0.00"
                  style={{ ...inputStyle, paddingLeft: "72px" }}
                />
              </div>
              {errors.pricing && <div style={errorStyle}>{errors.pricing}</div>}
            </div>
          )}

        </div>{/* end scrollable fields */}

          {/* Sticky submit footer — always visible, above mobile bottom nav */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #F3F4F6",
              background: "#fff",
              flexShrink: 0,
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "14px",
                background: submitting ? "#e89fb7" : "#C0335A",
                color: "#fff",
                border: "none",
                borderRadius: "9999px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {submitting
              ? (isEditMode ? "Saving..." : "Creating...")
              : (isEditMode ? "Save Changes" : "Create Subscription")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateSubscriptionModal;
